import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { categoriesApi, checklistsApi } from '@/lib/api';
import { 
  Tag, ListChecks, CaretDown, CaretRight, Plus, Trash, 
  PencilSimple, CheckSquare, X, Link as LinkIcon
} from '@phosphor-icons/react';

export const ProductSettings = () => {
  // Categories state
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newIssueName, setNewIssueName] = useState('');
  const [categoryWarrantyInputs, setCategoryWarrantyInputs] = useState({});

  // Checklists state
  const [checklists, setChecklists] = useState([]);
  const [loadingChecklists, setLoadingChecklists] = useState(true);
  const [expandedChecklist, setExpandedChecklist] = useState(null);
  const [newChecklistName, setNewChecklistName] = useState('');
  const [newItemText, setNewItemText] = useState('');
  const [newItemMandatory, setNewItemMandatory] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchChecklists();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await categoriesApi.list();
      setCategories(data || []);
    } catch (e) {
      toast.error('Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchChecklists = async () => {
    try {
      const { data } = await checklistsApi.list();
      setChecklists(data || []);
    } catch (e) {
      toast.error('Failed to load checklists');
    } finally {
      setLoadingChecklists(false);
    }
  };

  const handleUpdateCategoryConfig = async (categoryId, field, value) => {
    try {
      const payload = {};
      if (field === 'warranty_months') {
        payload.warranty_months = value !== undefined ? (parseInt(value, 10) || 12) : 12;
      }
      await categoriesApi.update(categoryId, payload);
      fetchCategories();
      toast.success('Category configuration updated successfully!');
    } catch (e) {
      toast.error('Failed to update category configuration');
    }
  };

  // Category CRUD
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await categoriesApi.create({ name: newCategoryName.trim() });
      setNewCategoryName('');
      fetchCategories();
      toast.success('Category created');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!confirm('Delete this category and all its issue types?')) return;
    try {
      await categoriesApi.delete(id);
      fetchCategories();
      toast.success('Category deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleAddIssue = async (categoryId) => {
    if (!newIssueName.trim()) return;
    try {
      await categoriesApi.createIssue(categoryId, { label: newIssueName.trim() });
      setNewIssueName('');
      fetchCategories();
      toast.success('Issue type added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add issue type');
    }
  };

  const handleDeleteIssue = async (issueId) => {
    try {
      await categoriesApi.deleteIssue(issueId);
      fetchCategories();
      toast.success('Issue type deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  // Link checklist to category
  const handleLinkChecklist = async (categoryId, checklistId) => {
    try {
      await categoriesApi.update(categoryId, { checklist_id: checklistId || null });
      fetchCategories();
      toast.success('Checklist linked to category');
    } catch (e) {
      toast.error('Failed to link checklist');
    }
  };

  // Checklist CRUD
  const handleAddChecklist = async () => {
    if (!newChecklistName.trim()) return;
    try {
      await checklistsApi.create({ name: newChecklistName.trim() });
      setNewChecklistName('');
      fetchChecklists();
      toast.success('Checklist created');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create checklist');
    }
  };

  const handleDeleteChecklist = async (id) => {
    if (!confirm('Delete this checklist and all its items?')) return;
    try {
      await checklistsApi.delete(id);
      fetchChecklists();
      toast.success('Checklist deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleAddItem = async (checklistId) => {
    if (!newItemText.trim()) return;
    try {
      await checklistsApi.createItem(checklistId, { label: newItemText.trim(), is_mandatory: newItemMandatory });
      setNewItemText('');
      setNewItemMandatory(false);
      fetchChecklists();
      toast.success('Item added');
    } catch (e) {
      toast.error('Failed to add item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await checklistsApi.deleteItem(itemId);
      fetchChecklists();
      toast.success('Item deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-8">
      {/* Product Categories & Issue Types */}
      <Card className="p-6 border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Tag size={20} weight="bold" className="text-primary" />
            <h2 className="text-lg font-bold">Product Categories & Issue Types</h2>
          </div>
        </div>

        {/* Add Category */}
        <div className="flex gap-2 mb-4">
          <Input
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="New category name..."
            className="max-w-xs"
            onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
          />
          <Button size="sm" onClick={handleAddCategory} disabled={!newCategoryName.trim()}>
            <Plus size={14} className="mr-1" /> Add Category
          </Button>
        </div>

        {/* Category List */}
        {loadingCategories ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />)}
          </div>
        ) : categories.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No categories yet</p>
        ) : (
          <div className="space-y-2">
            {categories.map(cat => (
              <div key={cat.id} className="border rounded-lg">
                <div
                  className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-muted/30"
                  onClick={() => setExpandedCategory(expandedCategory === cat.id ? null : cat.id)}
                >
                  {expandedCategory === cat.id ? <CaretDown size={14} /> : <CaretRight size={14} />}
                  <span className="font-semibold text-sm flex-1">{cat.name}</span>
                  <span className="text-xs text-muted-foreground mr-2">
                    {cat.issues?.length || 0} issues
                  </span>
                  {/* Checklist link */}
                  <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                    {cat.checklist_id ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 border border-emerald-200 text-emerald-700">
                        <CheckSquare size={10} weight="fill" />
                        {checklists.find(cl => cl.id === cat.checklist_id)?.name || 'Checklist linked'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 border border-amber-200 text-amber-600">
                        No checklist
                      </span>
                    )}
                    <Select
                      value={cat.checklist_id || 'none'}
                      onValueChange={(v) => handleLinkChecklist(cat.id, v === 'none' ? null : v)}
                    >
                      <SelectTrigger className="w-[130px] h-7 text-xs" onClick={(e) => e.stopPropagation()}>
                        <LinkIcon size={12} className="mr-1 shrink-0" />
                        <SelectValue placeholder="Attach…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Remove checklist</SelectItem>
                        {checklists.map(cl => (
                          <SelectItem key={cl.id} value={cl.id}>{cl.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-400 hover:text-red-600"
                    onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id); }}
                  >
                    <Trash size={14} />
                  </Button>
                </div>

                 {expandedCategory === cat.id && (
                  <div className="px-4 pb-4 pt-3 border-t bg-slate-50/50 space-y-4">
                    {/* Category Configuration */}
                    <div className="bg-white p-3 rounded-lg border border-slate-200 space-y-3 shadow-sm">
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b pb-1">
                        Category Specifications
                      </h4>
                      <div className="max-w-xs space-y-1.5">
                        <Label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                          Warranty Duration (Months)
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            min="0"
                            value={categoryWarrantyInputs[cat.id] !== undefined ? categoryWarrantyInputs[cat.id] : cat.warranty_months}
                            onChange={(e) => setCategoryWarrantyInputs({ ...categoryWarrantyInputs, [cat.id]: e.target.value })}
                            className="h-9 text-xs"
                          />
                          <Button 
                            size="sm"
                            onClick={() => handleUpdateCategoryConfig(cat.id, 'warranty_months', categoryWarrantyInputs[cat.id])}
                            className="h-9 text-xs font-bold text-white bg-slate-800 hover:bg-slate-900"
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Issues */}
                    <div>
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2">
                        Issue Types
                      </h4>
                    <div className="space-y-1 mb-3">
                      {(cat.issues || []).map(issue => (
                        <div key={issue.id} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                          <span className="flex-1 text-slate-600">• {issue.label}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-400 hover:text-red-600"
                            onClick={() => handleDeleteIssue(issue.id)}
                          >
                            <X size={12} />
                          </Button>
                        </div>
                      ))}
                    </div>
                    {/* Add Issue */}
                    <div className="flex gap-2">
                      <Input
                        value={newIssueName}
                        onChange={(e) => setNewIssueName(e.target.value)}
                        placeholder="New issue type..."
                        className="text-sm h-8"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddIssue(cat.id)}
                      />
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => handleAddIssue(cat.id)}>
                        <Plus size={12} className="mr-1" /> Add
                      </Button>
                    </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Service Checklists */}
      <Card className="p-6 border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ListChecks size={20} weight="bold" className="text-primary" />
            <h2 className="text-lg font-bold">Service Checklists</h2>
          </div>
        </div>

        {/* Add Checklist */}
        <div className="flex gap-2 mb-4">
          <Input
            value={newChecklistName}
            onChange={(e) => setNewChecklistName(e.target.value)}
            placeholder="New checklist name..."
            className="max-w-xs"
            onKeyDown={(e) => e.key === 'Enter' && handleAddChecklist()}
          />
          <Button size="sm" onClick={handleAddChecklist} disabled={!newChecklistName.trim()}>
            <Plus size={14} className="mr-1" /> Add Checklist
          </Button>
        </div>

        {/* Checklist List */}
        {loadingChecklists ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />)}
          </div>
        ) : checklists.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No checklists yet</p>
        ) : (
          <div className="space-y-2">
            {checklists.map(cl => (
              <div key={cl.id} className="border rounded-lg">
                <div
                  className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-muted/30"
                  onClick={() => setExpandedChecklist(expandedChecklist === cl.id ? null : cl.id)}
                >
                  {expandedChecklist === cl.id ? <CaretDown size={14} /> : <CaretRight size={14} />}
                  <CheckSquare size={16} className="text-emerald-500" />
                  <span className="font-semibold text-sm flex-1">{cl.name}</span>
                  <span className="text-xs text-muted-foreground mr-2">
                    {cl.items?.length || 0} items
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-400 hover:text-red-600"
                    onClick={(e) => { e.stopPropagation(); handleDeleteChecklist(cl.id); }}
                  >
                    <Trash size={14} />
                  </Button>
                </div>

                {expandedChecklist === cl.id && (
                  <div className="px-4 pb-3 pt-1 border-t bg-slate-50/50">
                    {/* Items */}
                    <div className="space-y-1 mb-3">
                      {(cl.items || []).map(item => (
                        <div key={item.id} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                          <CheckSquare size={14} className={item.is_mandatory ? 'text-amber-500' : 'text-slate-400'} />
                          <span className="flex-1 text-slate-600">{item.label}</span>
                          {item.is_mandatory && (
                            <span className="text-[10px] uppercase font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                              Required
                            </span>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-400 hover:text-red-600"
                            onClick={() => handleDeleteItem(item.id)}
                          >
                            <X size={12} />
                          </Button>
                        </div>
                      ))}
                    </div>
                    {/* Add Item */}
                    <div className="flex items-center gap-2">
                      <Input
                        value={newItemText}
                        onChange={(e) => setNewItemText(e.target.value)}
                        placeholder="New checklist item..."
                        className="text-sm h-8 flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddItem(cl.id)}
                      />
                      <label className="flex items-center gap-1.5 text-xs text-slate-600 font-medium cursor-pointer whitespace-nowrap select-none">
                        <input
                          type="checkbox"
                          checked={newItemMandatory}
                          onChange={(e) => setNewItemMandatory(e.target.checked)}
                          className="rounded text-primary focus:ring-primary h-3.5 w-3.5 border-slate-300"
                        />
                        Mandatory
                      </label>
                      <Button size="sm" variant="outline" className="h-8 text-xs font-bold shrink-0" onClick={() => handleAddItem(cl.id)}>
                        <Plus size={12} className="mr-1" /> Add
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
